import React from 'react';
import { useIntl } from 'react-intl';
import { ErrorMessage, Field } from '@atlaskit/form';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import TextField from '@atlaskit/textfield';
import Tooltip from '@atlaskit/tooltip';
import { useConditionalMessage } from '@atlassian/admin-layout';
import { HELPCENTER_NAME_CHARACTER_LIMIT } from '../../../constants';
import errorMessages from '../messages';
import messages from './messages';
const SLUG_VALIDATION_REGEX = /^[a-zA-Z0-9_-]{1,30}$/;

export interface HelpCenterFormFieldsProps {
	name?: string;
	slug?: string;
	urlWithoutSlug: string;
	mode: 'CREATE' | 'EDIT';
}
const SlugInfo = () => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={slugWarningStyles}>
			<Tooltip content={formatMessage(messages.helpCenterSlugInfoTooltip)} position="top-start">
				<StatusInformationIcon label={formatMessage(messages.helpCenterSlugInfoLabel)} />
			</Tooltip>
		</Box>
	);
};
export const HelpCenterFormFields = ({
	name,
	slug,
	urlWithoutSlug,
	mode,
}: HelpCenterFormFieldsProps) => {
	const { formatMessage } = useIntl();
	const nameLabel = useConditionalMessage(
		messages.helpHubNameTextLabel,
		messages.helpCenterNameTextLabel,
	);
	const urlLabel = useConditionalMessage(
		messages.helpHubSlugTextLabel,
		messages.helpCenterSlugTextLabel,
	);

	const validateHelpCenterName = (value: string | undefined) => {
		if (!value || value.length <= 0) {
			return formatMessage(errorMessages.helpCenterNameRequiredError);
		}
		if (value && value.length > HELPCENTER_NAME_CHARACTER_LIMIT) {
			return formatMessage(errorMessages.helpCenterNameCharacterLimitError);
		}
		return undefined;
	};

	const validateHelpCenterSlug = (value: string | undefined) => {
		if (!value || value.length <= 0) {
			return formatMessage(errorMessages.helpCenterSlugRequiredError);
		}
		if (value && value.length > HELPCENTER_NAME_CHARACTER_LIMIT) {
			return formatMessage(errorMessages.helpCenterSlugCharacterLimitError);
		}
		if (!SLUG_VALIDATION_REGEX.test(value)) {
			return formatMessage(errorMessages.helpCenterSlugSpecialCharacterError);
		}
		return undefined;
	};

	return (
		<Stack space="space.250">
			<Field
				name="name"
				key="name"
				label={formatMessage(nameLabel)}
				defaultValue={name ?? ''}
				validate={validateHelpCenterName}
				isRequired
			>
				{({ fieldProps, error }) => (
					<Box paddingBlockStart="space.150">
						<TextField
							{...fieldProps}
							autoComplete="off"
							autoFocus
							placeholder={formatMessage(messages.helpCenterNameTextFieldPlaceholder)}
							testId="help-centers-management-name-field"
						/>

						{error && <ErrorMessage>{error}</ErrorMessage>}
					</Box>
				)}
			</Field>
			{mode === 'CREATE' || slug ? (
				<Field
					name="slug"
					key="slug"
					label={formatMessage(urlLabel)}
					defaultValue={slug ?? ''}
					validate={validateHelpCenterSlug}
					elementAfterLabel={mode !== 'CREATE' && <SlugInfo />}
					isRequired
				>
					{({ fieldProps, error }) => (
						<Inline grow="fill" shouldWrap>
							<Box paddingBlockStart="space.100" paddingInlineEnd="space.100">
								<Text color="color.text">
									{urlWithoutSlug}
									{'/'}
								</Text>
							</Box>
							<Box xcss={slugTextFieldStyles}>
								<TextField
									autoComplete="off"
									{...fieldProps}
									placeholder={formatMessage(messages.helpCenterSlugTextFieldPlaceholder)}
									testId="help-centers-management-slug-field"
								/>

								{error && <ErrorMessage>{error}</ErrorMessage>}
							</Box>
						</Inline>
					)}
				</Field>
			) : null}
		</Stack>
	);
};

const slugTextFieldStyles = xcss({ width: '10%', flexGrow: 1 });

const slugWarningStyles = xcss({
	display: 'inline-flex',
	marginLeft: 'space.050',
	verticalAlign: 'middle',
});
